import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Download, Search, CheckCircle, ExternalLink, FileBarChart, Sparkles, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { ExecutiveValueReport } from '../components/ExecutiveValueReport';
import { openReportBuilderPhoebe } from '../components/GlobalPhoebeChat';
export function ReportsPage() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedResult, setSelectedResult] = useState<{
    id: number;
    name: string;
    type: string;
    initials: string;
  } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  // Quick options shown on focus, grouped by section
  const quickOptionSections = [{
    label: 'Reports',
    items: [{
      id: 100,
      name: 'All Skills Report',
      type: 'Report',
      initials: 'AS'
    }, {
      id: 101,
      name: 'All Learners Report',
      type: 'Report',
      initials: 'AL'
    }, {
      id: 102,
      name: 'Reflections Survey Report',
      type: 'Report',
      initials: 'RS'
    }, {
      id: 103,
      name: 'Assessment Report',
      type: 'Report',
      initials: 'AR'
    }]
  }, {
    label: 'Learners',
    items: [{
      id: 200,
      name: 'John Fisher',
      type: 'Learner',
      initials: 'JF'
    }, {
      id: 201,
      name: 'David Taylor',
      type: 'Learner',
      initials: 'DT'
    }, {
      id: 202,
      name: 'Tom Zoldos',
      type: 'Learner',
      initials: 'TZ'
    }]
  }, {
    label: 'Skills',
    items: [{
      id: 300,
      name: 'HTML Basics',
      type: 'Skill',
      initials: 'HB'
    }, {
      id: 301,
      name: 'CSS Fundamentals',
      type: 'Skill',
      initials: 'CF'
    }, {
      id: 302,
      name: 'JavaScript Basics',
      type: 'Skill',
      initials: 'JB'
    }]
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    if (isUserMenuOpen || isSearchFocused) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isSearchFocused]);
  // Searchable items (users, skills, reports)
  const searchableItems = [{
    id: 1,
    name: 'Linda Taylor',
    type: 'User',
    initials: 'LT'
  }, {
    id: 2,
    name: 'John Smith',
    type: 'User',
    initials: 'JS'
  }, {
    id: 3,
    name: 'Sarah Johnson',
    type: 'User',
    initials: 'SJ'
  }, {
    id: 4,
    name: 'Michael Chen',
    type: 'User',
    initials: 'MC'
  }, {
    id: 5,
    name: 'Emily Davis',
    type: 'User',
    initials: 'ED'
  }, {
    id: 6,
    name: 'Alex Rivera',
    type: 'User',
    initials: 'AR'
  }, {
    id: 7,
    name: 'Lisa Wong',
    type: 'User',
    initials: 'LW'
  }, {
    id: 8,
    name: 'HTML Basics',
    type: 'Skill',
    initials: 'HB'
  }, {
    id: 9,
    name: 'CSS Fundamentals',
    type: 'Skill',
    initials: 'CF'
  }, {
    id: 10,
    name: 'JavaScript Basics',
    type: 'Skill',
    initials: 'JB'
  }, {
    id: 11,
    name: 'Responsive Design',
    type: 'Skill',
    initials: 'RD'
  }, {
    id: 12,
    name: 'Monthly Analytics Report',
    type: 'Report',
    initials: 'MA'
  }, {
    id: 13,
    name: 'Learner Progress Summary',
    type: 'Report',
    initials: 'LP'
  }, {
    id: 14,
    name: 'Skills Completion Report',
    type: 'Report',
    initials: 'SC'
  }, {
    id: 15,
    name: 'Assessment Performance',
    type: 'Report',
    initials: 'AP'
  }];
  const searchResults = searchQuery.length > 0 ? searchableItems.filter(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()) || item.type.toLowerCase().includes(searchQuery.toLowerCase())) : [];
  const reportTemplates = [{
    id: 1,
    name: 'Learner Progress Report',
    description: 'Track individual learner progress across all skills',
    icon: Users,
    color: 'from-[#30718D] to-[#265a70]',
    fields: ['Learner Name', 'Skill', 'Date Range']
  }, {
    id: 2,
    name: 'Skills Analytics',
    description: 'Comprehensive analytics for specific skills',
    icon: BarChart3,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Skill', 'Date Range', 'Metrics']
  }, {
    id: 3,
    name: 'Assessment Results',
    description: 'Detailed assessment performance and scores',
    icon: FileBarChart,
    color: 'from-purple-500 to-purple-600',
    fields: ['Assessment', 'Date Range', 'Learner Group']
  }, {
    id: 4,
    name: 'Completion Summary',
    description: 'Overview of skill completions and certifications',
    icon: CheckCircle,
    color: 'from-[#007558] to-[#005a44]',
    fields: ['Date Range', 'Skill Category']
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2707" data-mp="3uwi3ijh5ol4m">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2708" data-mp="3qv50eh5g6w1e">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2709" data-mp="8me78cst2ptn">
          <div className="flex items-center h-16" data-id="element-2710" data-mp="3om6cta6ntdb">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2711" data-mp="3mjhwdooi8ap">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2712" data-mp="375316qpdq3td">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2713" data-mp="37300ncqlosae">
                <Home size={18} data-id="element-2714" data-mp="2tdeoomb7xdbv" />
                <span className="hidden sm:inline" data-id="element-2715" data-mp="2tcmvfi53fror">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2716" data-mp="2ofd602hpfg0x">
                <BarChart3 size={18} data-id="element-2717" data-mp="18okf89k62i8l" />
                <span className="hidden sm:inline" data-id="element-2718" data-mp="uw4j7sponkh6">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2719" data-mp="yxfz36ir8rc6">
                <FileText size={18} data-id="element-2720" data-mp="1rllmy253uwg8" />
                <span className="hidden sm:inline" data-id="element-2721" data-mp="1dmcddlben0l8">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2722" data-mp="1doou5v5ff4wp">
                <UserCog size={18} data-id="element-2723" data-mp="76jwqzubm535" />
                <span className="hidden sm:inline" data-id="element-2724" data-mp="77l9d8qzizmz">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2725" data-mp="tv0ct08dm1uf">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2726" data-mp="tx4nc10s5z3o">
                <Bell size={20} data-id="element-2727" data-mp="1hkm8hdpjhyxo" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2728" data-mp="1hjjid801k0it"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2729" data-mp="1cnm18zgyeq4z">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2730" data-mp="17t8eflgcxrbx">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2731" data-mp="20c5b3jfnjaz6">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2732" data-mp="22wjzq56mrb1h">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2733" data-mp="2ctg3jxll38zp" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2734" data-mp="2hssx116yneb9">
                    <div className="py-2" data-id="element-2735" data-mp="2ltuogvumym2o">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2736" data-mp="2qsxw8o2036ec">
                        <Users size={18} className="text-gray-600" data-id="element-2737" data-mp="30hz9w7l2pbp5" />
                        <span className="font-medium text-gray-900" data-id="element-2738" data-mp="35idjvv68ivpt">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2739" data-mp="39jyt96jk8rv9">
                        <Home size={18} className="text-gray-600" data-id="element-2740" data-mp="q3vb6wfu4ucu" />
                        <span className="font-medium text-gray-900" data-id="element-2741" data-mp="q224jh582a4u">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2742" data-mp="zuq0k9odoa2i">
                        <UserCog size={18} className="text-gray-600" data-id="element-2743" data-mp="14vdnok9hk6rg" />
                        <span className="font-medium text-gray-900" data-id="element-2744" data-mp="18t1o22ty6rqq">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-2745" data-mp="1it29bubdnhxk"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2746" data-mp="3fl2if94hx8ew">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2747" data-mp="3kl8ed1hsth2s">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2748" data-mp="3vzicg7g3qsy1"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2749" data-mp="3vxfn7pmrx3ud"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2750" data-mp="3r0yra99c1l6q"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2751" data-mp="3lzrhk4nmqbxf">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2752" data-mp="25pfeo2tftqtn">
        {/* Header */}
        <div className="mb-8" data-id="element-2753" data-mp="11p8r97ddyslh">
          <div className="flex items-center gap-2 mb-2" data-id="element-2754" data-mp="11piieya635sr">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-id="element-2755" data-mp="11mwct46u70d6">
              Reports
            </h1>
            <button type="button" data-phoebe-tour="report-builder" onClick={openReportBuilderPhoebe} aria-label="Ask Phoebe to help build a report" title="Build a report with Phoebe" className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#30718D]/30 bg-white text-[#30718D] shadow-sm transition-colors hover:bg-[#30718D]/10 focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2" data-id="element-2756" data-mp="sokbo88cgk6h">
              <Sparkles size={15} aria-hidden="true" data-id="element-2757" data-mp="3r5wnknatdij0" />
            </button>
          </div>
          <p className="text-gray-600 text-sm sm:text-base" data-id="element-2758" data-mp="eu1ucl7bav">
            Generate, download, and manage your learning platform reports
          </p>
        </div>

        <ExecutiveValueReport data-id="element-2759" data-mp="3clap00j96iw4" />

        {/* Report Templates */}
        <div data-phoebe-tour="reports" className="mb-8" data-id="element-2760" data-mp="38j78uyln2q4i">
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2761" data-mp="3mhdnmwewbzeb">
            Generate New Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-2762" data-mp="3hiavudnxfpoa">
            {reportTemplates.map((template, index) => {
            const Icon = template.icon;
            return <motion.div key={template.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + index * 0.1
            }} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2763" data-mp="3rhjngzvbks7h" data-mpi={index}>
                  <div className="flex items-start gap-4" data-id="element-2764" data-mp="27kaslgb2hf28">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${template.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`} data-id="element-2765" data-mp="2civv5mdskatu">
                      <Icon size={24} className="text-white" data-id="element-2766" data-mp="2m8xksilsm1av" />
                    </div>
                    <div className="flex-1" data-id="element-2767" data-mp="1on05sv6mge3o">
                      <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-[#30718D] transition-colors" data-id="element-2768" data-mp="1tktbln40j63f">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-0" data-id="element-2769" data-mp="1ylz35hlxsnbd">
                        {template.description}
                      </p>
                      <div className="flex justify-end mt-4" data-id="element-2770" data-mp="39zkw7dux3926">
                        <button className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white text-sm font-medium rounded-lg hover:bg-[#c02f1f] transition-colors" data-id="element-2771" data-mp="3a1e0wnlyzj7j">
                          <Download size={14} data-id="element-2772" data-mp="13a9etmozpcr8" />
                          Download Report
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Download Reports */}
        <div data-phoebe-tour="download-reports" className="bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-2773" data-mp="ojsds4g9oouv">
          <div className="p-4 sm:p-6" data-id="element-2774" data-mp="jkgw8t82tfc8">
            <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-2775" data-mp="fkyhkf6um0hu">
              Download Reports
            </h2>

            {/* Search and Download */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3" data-id="element-2776" data-mp="anydg9tk77td">
              <div className="relative flex-1" ref={searchContainerRef} data-id="element-2777" data-mp="5p4wu8iz70oz">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2778" data-mp="24zmm5u976e5b" />
                <input type="text" placeholder="Search for skills, learners or reports" value={selectedResult ? selectedResult.name : searchQuery} onChange={e => {
                if (selectedResult) {
                  setSelectedResult(null);
                }
                setSearchQuery(e.target.value);
              }} onFocus={() => setIsSearchFocused(true)} className={`w-full pl-9 ${selectedResult ? 'pr-9' : 'pr-3'} py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent text-sm ${selectedResult ? 'border-[#30718D]/30 bg-[#30718D]/5 text-[#30718D] font-medium' : 'border-gray-300'}`} data-id="element-2779" data-mp="3ugqi1d25nr64" />
                {selectedResult && <button onClick={() => {
                setSelectedResult(null);
                setSearchQuery('');
              }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2780" data-mp="2rtnc9xcvwqs1">
                    <X size={16} data-id="element-2781" data-mp="19wjyf1kmmd3i" />
                  </button>}

                {/* Dropdown: Quick Options or Search Results */}
                {isSearchFocused && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-80 overflow-y-auto" data-id="element-2782" data-mp="1jwaq6vhsydzw">
                    {searchQuery.length === 0 ? <div data-id="element-2783" data-mp="r8586gi4v33r">
                        {quickOptionSections.map(section => <div key={section.label} data-id="element-2784" data-mp="14z9z8u7fqn2h">
                            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100" data-id="element-2785" data-mp="106gva8jsjirg">
                              <span className="text-xs font-medium text-black uppercase tracking-wide" data-id="element-2786" data-mp="cgcv6b5ta157">
                                {section.label}
                              </span>
                            </div>
                            {section.items.map(option => <button key={option.id} onClick={() => {
                      setSelectedResult(selectedResult?.id === option.id ? null : option);
                      setIsSearchFocused(false);
                    }} className={`w-full flex items-center px-4 py-2.5 text-left text-sm transition-colors border-b border-gray-100 last:border-b-0 ${selectedResult?.id === option.id ? 'bg-[#30718D]/10 text-[#30718D] font-semibold' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-2787" data-mp="128wt6vo38cq4">
                                <span className="flex-1" data-id="element-2788" data-mp="23yil7hantnpy">{option.name}</span>
                                {selectedResult?.id === option.id && <CheckCircle size={16} className="text-[#30718D]" data-id="element-2789" data-mp="2e0w394gqw7eh" />}
                              </button>)}
                          </div>)}
                      </div> : searchResults.length > 0 ? searchResults.map(result => <button key={result.id} onClick={() => {
                  setSelectedResult(selectedResult?.id === result.id ? null : result);
                  setIsSearchFocused(false);
                }} className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${selectedResult?.id === result.id ? 'bg-[#30718D]/10' : 'hover:bg-gray-50'}`} data-id="element-2790" data-mp="2rux5i86wocjl">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium ${selectedResult?.id === result.id ? 'bg-[#30718D] text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-2791" data-mp="116uj6qp90anp">
                            {result.initials}
                          </div>
                          <div className="flex-1" data-id="element-2792" data-mp="1oyrjbkjc5e8z">
                            <div className="font-medium text-gray-900 text-sm" data-id="element-2793" data-mp="1jxc2k4o6xtwe">
                              {result.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-2794" data-mp="1f4iy7hwmwnku">
                              {result.type}
                            </div>
                          </div>
                          {selectedResult?.id === result.id && <CheckCircle size={18} className="text-[#30718D]" data-id="element-2795" data-mp="22veuosc1c6yf" />}
                        </button>) : <div className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-2796" data-mp="2f5w2yno8w8me">
                        No results found for "{searchQuery}"
                      </div>}
                  </div>}
              </div>
              <button disabled={!selectedResult} className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedResult ? 'bg-[#DB3725] text-white hover:bg-[#c02f1f] cursor-pointer' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-2797" data-mp="2p2se715j38un">
                <Download size={16} data-id="element-2798" data-mp="37v3d6jx2amnh" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}